import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { SparkRenderer, SplatMesh } from '@sparkjsdev/spark'
import { MODEL_URL } from './settings/appSettings.js'
import { createSettingsSnapshot, loadStoredSettings, saveSettingsSnapshot } from './settings/settingsStorage.js'
import { createOrbitPersistence } from './camera/orbitPersistence.js'
import { createFishEyePass } from './fish-eye/fishEyePass.js'
import { createEffectUniforms, createEffectsModifier, syncEffectUniforms } from './effects/effectsModifier.js'
import { createSwagSpiral } from './spiral/swagSpiral.js'
import { updateSmoothedNumericValues } from './runtime/smoothing.js'
import { createWireframeOverlay } from './utils/wireframeOverlay.js'

const canvas = document.querySelector('canvas.webgl')
const storedSettings = loadStoredSettings()
const isAnimatorMode = window.location.pathname.replace(/\/$/, '').endsWith('/animator')

const scene = new THREE.Scene()
scene.background = new THREE.Color(0xffffff)

const camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.01, 2000)
camera.position.set(0, 0, 1.5)

const renderer = new THREE.WebGLRenderer({ canvas, antialias: false })
renderer.setSize(window.innerWidth, window.innerHeight)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 1

const spark = new SparkRenderer({ renderer })
scene.add(spark)

const cameraParams = { ...storedSettings.camera }
const guiFolderParams = { ...storedSettings.gui.folders }
const effectParams = {
  enabled: storedSettings.effect.enabled,
  family: storedSettings.effect.family,
  effect: storedSettings.effect.effect,
  intensity: storedSettings.effect.intensity,
  speed: storedSettings.effect.speed,
  autoReplay: storedSettings.effect.autoReplay,
  replayDuration: storedSettings.effect.replayDuration,
  pause: storedSettings.effect.pause,
  resetAnimation: () => {
    elapsedTime = 0
    effectUniforms.animateT.value = 0
    saveSettings()
  }
}
const fishEyeParams = { ...storedSettings.fishEye }
const spiralParams = { ...storedSettings.spiral }
const utilityParams = { ...storedSettings.utility }
const effectRuntime = {
  intensity: effectParams.intensity,
  speed: effectParams.speed,
  replayDuration: effectParams.replayDuration
}
const cameraRuntime = {
  autoRotateSpeed: cameraParams.autoRotateSpeed
}
const fishEyeRuntime = {
  strength: fishEyeParams.strength,
  radius: fishEyeParams.radius,
  distortionPower: fishEyeParams.distortionPower,
  blend: fishEyeParams.blend,
  chromaticAberration: fishEyeParams.chromaticAberration,
  zoom: fishEyeParams.zoom,
  centerX: fishEyeParams.centerX,
  centerY: fishEyeParams.centerY,
  edgeFeather: fishEyeParams.edgeFeather,
  edgeColor: fishEyeParams.edgeColor
}

let elapsedTime = 0
let lastFrameTime = 0
let theatreAnimator = null
const effectUniforms = createEffectUniforms(effectParams)

const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
controls.autoRotate = cameraParams.autoRotate
controls.autoRotateSpeed = cameraParams.autoRotateSpeed

const orbitPersistence = createOrbitPersistence({
  camera,
  controls,
  initialOrbitSettings: cameraParams.orbit,
  onSaveSettings: saveSettings
})
const fishEyePass = createFishEyePass({ renderer })
const swagSpiral = createSwagSpiral({ scene, params: spiralParams })
const wireframeOverlay = createWireframeOverlay({ scene, params: utilityParams })

function saveSettings() {
  saveSettingsSnapshot(
    createSettingsSnapshot({
      effectParams,
      cameraParams,
      orbitSettings: orbitPersistence.getPersistedOrbitSettings(),
      fishEyeParams,
      spiralParams,
      utilityParams,
      guiFolderParams
    })
  )
}

function persistEffectSettings() {
  syncEffectUniforms({ effectParams, effectRuntime, uniforms: effectUniforms })
  saveSettings()
}

function persistFishEyeSettings() {
  saveSettings()
}

function persistFishEyeSamplingSettings() {
  fishEyePass.updateSize(fishEyeParams)
  saveSettings()
}

function persistCameraSettings() {
  controls.autoRotate = cameraParams.autoRotate
  saveSettings()
}

function persistSpiralSettings() {
  swagSpiral.updateLine()
  wireframeOverlay.refresh()
  saveSettings()
}

function persistWireframeSettings() {
  wireframeOverlay.setEnabled(utilityParams.wireframe)
  wireframeOverlay.refresh()
  saveSettings()
}

function getWorldBounds(mesh) {
  const localBox = mesh.getBoundingBox()
  const worldBox = new THREE.Box3()
  const corner = new THREE.Vector3()

  for (const x of [localBox.min.x, localBox.max.x]) {
    for (const y of [localBox.min.y, localBox.max.y]) {
      for (const z of [localBox.min.z, localBox.max.z]) {
        corner.set(x, y, z).applyMatrix4(mesh.matrixWorld)
        worldBox.expandByPoint(corner)
      }
    }
  }

  return { localBox, worldBox }
}

function frameLoadedMesh(mesh) {
  mesh.updateMatrixWorld(true)
  // Local bounds drive shader reveal, world bounds drive camera framing.
  const { localBox, worldBox } = getWorldBounds(mesh)
  const localCenter = localBox.getCenter(new THREE.Vector3())
  const localSize = localBox.getSize(new THREE.Vector3())
  effectUniforms.revealCenter.value.copy(localCenter)
  effectUniforms.revealRadius.value = Math.max(localSize.x, localSize.y, localSize.z, 0.01) * 0.5

  const center = new THREE.Vector3()
  const size = new THREE.Vector3()
  worldBox.getCenter(center)
  worldBox.getSize(size)
  swagSpiral.setModelBounds({ center, size })
  wireframeOverlay.refresh()

  const maxDim = Math.max(size.x, size.y, size.z, 0.01)
  const dist = maxDim * 1.4
  controls.target.copy(center)
  camera.position.copy(center)
  camera.position.x += dist * 0.35
  camera.position.y += dist * 0.2
  camera.position.z += dist
  camera.near = Math.max(0.001, dist * 0.0005)
  camera.far = dist * 50
  camera.updateProjectionMatrix()

  if (!orbitPersistence.applyOrbitSettings(storedSettings.camera.orbit)) {
    controls.update()
  }
  orbitPersistence.markReady()
  theatreAnimator?.setCameraOrbitBase()
}

syncEffectUniforms({ effectParams, effectRuntime, uniforms: effectUniforms })
fishEyePass.syncParams(fishEyeRuntime)
fishEyePass.updateSize(fishEyeParams)

if (!isAnimatorMode) {
  import('./gui/sparkGui.js').then(({ createSparkGui }) => {
    createSparkGui({
      effectParams,
      cameraParams,
      fishEyeParams,
      spiralParams,
      guiFolderParams,
      onSaveSettings: saveSettings,
      onPersistEffectSettings: persistEffectSettings,
      onPersistCameraSettings: persistCameraSettings,
      onPersistFishEyeSettings: persistFishEyeSettings,
      onPersistFishEyeSamplingSettings: persistFishEyeSamplingSettings,
      onPersistSpiralSettings: persistSpiralSettings,
      utilityParams,
      onPersistWireframeSettings: persistWireframeSettings
    })
  })
}

if (isAnimatorMode) {
  import('./animator/theatreAnimator.js').then(({ createTheatreAnimator }) => {
    theatreAnimator = createTheatreAnimator({
      camera,
      controls,
      cameraParams,
      cameraRuntime,
      effectParams,
      effectRuntime,
      fishEyeParams,
      fishEyeRuntime,
      spiralParams,
      utilityParams,
      effectUniforms,
      fishEyePass,
      swagSpiral,
      wireframeOverlay,
      syncEffectUniforms,
      saveSettings,
      getEffectTime: () => elapsedTime,
      setEffectTime: (value) => {
        elapsedTime = value
      }
    })
    theatreAnimator.setCameraOrbitBase()
  })
}

const splat = new SplatMesh({
  url: MODEL_URL,
  onLoad: frameLoadedMesh
})
// Y aligns the model horizontally; X fixes the upside-down file orientation.
splat.rotation.y = -Math.PI
splat.rotation.x = Math.PI
splat.objectModifier = createEffectsModifier(effectUniforms)
splat.updateGenerator()
scene.add(splat)

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight
  camera.updateProjectionMatrix()
  renderer.setSize(window.innerWidth, window.innerHeight)
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  fishEyePass.updateSize(fishEyeParams)
})

renderer.setAnimationLoop((frameTime) => {
  const deltaTime = lastFrameTime ? (frameTime - lastFrameTime) / 1000 : 0
  lastFrameTime = frameTime

  if (theatreAnimator) {
    theatreAnimator.applyFrame()
  } else {
    updateSmoothedNumericValues({
      deltaTime,
      effectParams,
      effectRuntime,
      cameraParams,
      cameraRuntime,
      fishEyeParams,
      fishEyeRuntime,
      controls,
      effectUniforms,
      syncFishEyeParams: fishEyePass.syncParams
    })
  }

  if (!theatreAnimator && !effectParams.pause) {
    elapsedTime += deltaTime * effectRuntime.speed
    if (effectParams.autoReplay) {
      elapsedTime %= Math.max(effectRuntime.replayDuration, 0.001)
    }
    effectUniforms.animateT.value = elapsedTime
  }

  splat.updateVersion()
  if (!theatreAnimator) {
    swagSpiral.update(deltaTime)
  }
  controls.update()

  if (fishEyeParams.enabled) {
    fishEyePass.render(scene, camera)
  } else {
    renderer.render(scene, camera)
  }
})
